document.addEventListener('DOMContentLoaded', () => {
	document.querySelectorAll('.gallery').forEach(initGallery);
});

function initGallery(gallery) {
	const track = gallery.querySelector('.gallery-track');
	const prevBtn = gallery.querySelector('.gallery-prev');
	const nextBtn = gallery.querySelector('.gallery-next');

	if (!track || !prevBtn || !nextBtn) return;

	const originalSlides = Array.from(track.querySelectorAll('.gallery-slide'));

	if (!originalSlides.length) return;

	const SPEED = 700;
	const AUTOPLAY_DELAY = 5000;
	const RESIZE_DEBOUNCE = 150;

	let renderedSlides = [];
	let slideWidths = [];
	let clonesBefore = 0;
	let logicalIndex = 0;
	let renderedIndex = 0;
	let isBuilt = false;
	let isPaused = false;
	let isTransitioning = false;
	let autoplayTimer = null;
	let resizeTimer = null;
	let lastGalleryWidth = Math.round(gallery.getBoundingClientRect().width);
	let buildToken = 0;

	build();

	nextBtn.addEventListener('click', () => {
		if (!isBuilt) return;

		stopAutoplay();
		slideToLogical(logicalIndex + 1);
		startAutoplay();
	});

	prevBtn.addEventListener('click', () => {
		if (!isBuilt) return;

		stopAutoplay();
		slideToLogical(logicalIndex - 1);
		startAutoplay();
	});

	gallery.addEventListener('mouseenter', pauseAutoplay);
	gallery.addEventListener('mouseleave', resumeAutoplay);
	gallery.addEventListener('focusin', pauseAutoplay);
	gallery.addEventListener('focusout', resumeAutoplay);

	track.addEventListener('transitionend', (event) => {
		if (event.target !== track || event.propertyName !== 'transform') return;

		isTransitioning = false;
		snapToRealSlide();
		startAutoplay();
	});

	if ('ResizeObserver' in window) {
		const resizeObserver = new ResizeObserver(scheduleRebuild);
		resizeObserver.observe(gallery);
	} else {
		window.addEventListener('resize', scheduleRebuild);
	}

	function scheduleRebuild() {
		const nextGalleryWidth = Math.round(gallery.getBoundingClientRect().width);

		if (nextGalleryWidth === lastGalleryWidth) return;

		lastGalleryWidth = nextGalleryWidth;
		clearTimeout(resizeTimer);
		resizeTimer = setTimeout(build, RESIZE_DEBOUNCE);
	}

	async function build() {
		const currentBuildToken = ++buildToken;

		stopAutoplay();
		isBuilt = false;
		isTransitioning = false;

		restoreOriginalSlides();
		clearSlideWidths(originalSlides);
		setPositionInstant(0);

		await waitForImages(originalSlides);
		await waitForLayout();

		if (currentBuildToken !== buildToken) return;

		const gap = getTrackGap();
		const originalWidths = measureSlides(originalSlides);
		const viewportWidth = gallery.getBoundingClientRect().width;

		if (!viewportWidth || originalWidths.some(width => !width)) {
			renderedSlides = originalSlides;
			slideWidths = originalWidths;
			return;
		}

		const cloneCount = getCloneCount(viewportWidth, originalWidths, gap);

		clonesBefore = cloneCount;
		renderedSlides = [
			...createBeforeClones(cloneCount),
			...originalSlides,
			...createAfterClones(cloneCount),
		];

		track.replaceChildren(...renderedSlides);
		applySlideWidths(renderedSlides, originalWidths);

		slideWidths = renderedSlides.map((slide) => slide.getBoundingClientRect().width);
		renderedIndex = clonesBefore + normalizeIndex(logicalIndex);
		isBuilt = true;

		setPositionInstant(getOffset(renderedIndex, gap));
		startAutoplay();
	}

	function restoreOriginalSlides() {
		track.replaceChildren(...originalSlides);
		clonesBefore = 0;
		renderedSlides = originalSlides;
		slideWidths = [];
	}

	function createBeforeClones(cloneCount) {
		const clones = [];

		for (let i = cloneCount; i > 0; i--) {
			const originalIndex = normalizeIndex(originalSlides.length - i);
			clones.push(createClone(originalSlides[originalIndex], originalIndex));
		}

		return clones;
	}

	function createAfterClones(cloneCount) {
		const clones = [];

		for (let i = 0; i < cloneCount; i++) {
			const originalIndex = normalizeIndex(i);
			clones.push(createClone(originalSlides[originalIndex], originalIndex));
		}

		return clones;
	}

	function createClone(slide, originalIndex) {
		const clone = slide.cloneNode(true);

		clone.setAttribute('aria-hidden', 'true');
		clone.dataset.originalIndex = originalIndex;

		return clone;
	}

	function applySlideWidths(slides, originalWidths) {
		slides.forEach((slide) => {
			const originalIndex = slide.dataset.originalIndex ?? originalSlides.indexOf(slide);
			const width = originalWidths[originalIndex];

			if (!width) return;

			slide.style.width = `${width}px`;
		});
	}

	function clearSlideWidths(slides) {
		slides.forEach((slide) => {
			slide.style.width = '';
		});
	}

	function measureSlides(slides) {
		return slides.map((slide) => slide.getBoundingClientRect().width);
	}

	function getCloneCount(viewportWidth, widths, gap) {
		const minWidth = Math.min(...widths);

		return Math.ceil(viewportWidth / (minWidth + gap)) + 2;
	}

	function getTrackGap() {
		const styles = window.getComputedStyle(track);
		const columnGap = parseFloat(styles.columnGap);
		const gap = parseFloat(styles.gap);

		if (!Number.isNaN(columnGap)) return columnGap;
		if (!Number.isNaN(gap)) return gap;

		return 0;
	}

	function getOffset(index, gap) {
		let offset = 0;

		for (let i = 0; i < index; i++) {
			offset += slideWidths[i] + gap;
		}

		const viewportWidth = gallery.getBoundingClientRect().width;
		const slideCenter = offset + slideWidths[index] / 2;

		return -(slideCenter - viewportWidth / 2);
	}

	function setPositionInstant(offset) {
		track.style.transition = 'none';
		track.style.transform = `translateX(${offset}px)`;
		track.offsetHeight;
	}

	function slideToLogical(nextLogicalIndex) {
		if (isTransitioning) return;

		const gap = getTrackGap();

		logicalIndex = normalizeIndex(nextLogicalIndex);
		renderedIndex = getRenderedIndexForMovement(nextLogicalIndex);
		isTransitioning = true;

		track.style.transition = `transform ${SPEED}ms ease`;
		track.style.transform = `translateX(${getOffset(renderedIndex, gap)}px)`;
	}

	function getRenderedIndexForMovement(nextLogicalIndex) {
		const direction = nextLogicalIndex > getLogicalIndexFromRenderedIndex() ? 1 : -1;

		if (nextLogicalIndex < 0) return renderedIndex - 1;
		if (nextLogicalIndex >= originalSlides.length) return renderedIndex + 1;

		return renderedIndex + direction;
	}

	function getLogicalIndexFromRenderedIndex() {
		return normalizeIndex(renderedIndex - clonesBefore);
	}

	function snapToRealSlide() {
		const minRealIndex = clonesBefore;
		const maxRealIndex = clonesBefore + originalSlides.length - 1;

		if (renderedIndex < minRealIndex || renderedIndex > maxRealIndex) {
			renderedIndex = clonesBefore + logicalIndex;
			setPositionInstant(getOffset(renderedIndex, getTrackGap()));
		}
	}

	function normalizeIndex(index) {
		return ((index % originalSlides.length) + originalSlides.length) % originalSlides.length;
	}

	function pauseAutoplay() {
		isPaused = true;
		stopAutoplay();
	}

	function resumeAutoplay() {
		isPaused = false;
		startAutoplay();
	}

	function startAutoplay() {
		stopAutoplay();

		if (!isBuilt || isPaused || originalSlides.length < 2) return;

		autoplayTimer = setTimeout(() => {
			slideToLogical(logicalIndex + 1);
		}, AUTOPLAY_DELAY);
	}

	function stopAutoplay() {
		clearTimeout(autoplayTimer);
		autoplayTimer = null;
	}

	function waitForImages(slides) {
		const images = slides.flatMap((slide) => Array.from(slide.querySelectorAll('img')));

		return Promise.all(images.map(waitForImage));
	}

	function waitForImage(img) {
		if (img.complete) {
			return img.decode ? img.decode().catch(() => {}) : Promise.resolve();
		}

		return new Promise((resolve) => {
			img.addEventListener('load', resolve, { once: true });
			img.addEventListener('error', resolve, { once: true });
		});
	}

	function waitForLayout() {
		return new Promise((resolve) => {
			requestAnimationFrame(() => {
				requestAnimationFrame(resolve);
			});
		});
	}
}
